// Currency-safe money formatting. Amounts in different currencies are never
// summed: callers pass a {CURRENCY: amount} map and get one entry per currency.

export function formatMoney(amount, currency = "USD", opts = {}) {
  const value = Number(amount) || 0;
  const code = String(currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: opts.decimals ?? 2,
      maximumFractionDigits: opts.decimals ?? 2,
    }).format(value);
  } catch {
    return `${value.toFixed(opts.decimals ?? 2)} ${code}`;
  }
}

// {EUR: 800, USD: 12.5} -> "€800.00 · $12.50"; empty -> fallback
export function formatCurrencyTotals(byCurrency, opts = {}) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => Number(v) !== 0 || opts.keepZero);
  if (!entries.length) return opts.empty ?? formatMoney(0, opts.defaultCurrency || "USD", opts);
  return entries
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .map(([code, v]) => formatMoney(v, code, opts))
    .join(" · ");
}

// Pick the currency with the largest absolute total (used to scope charts).
export function primaryCurrency(byCurrency, fallback = "USD") {
  const entries = Object.entries(byCurrency || {});
  if (!entries.length) return fallback;
  return entries.sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))[0][0];
}

const SYMBOL_CURRENCY = { "€": "EUR", "$": "USD", "£": "GBP" };
const CODE_RE = /\b(USD|EUR|GBP|CAD|AUD|CHF)\b/i;

// Same rule as backend revenue_sources.detect_currency: explicit code, then a
// symbol/code in the raw text, else USD *assumed* (flagged, never silently).
export function detectCurrency(explicit, ...raw) {
  if (explicit) return { currency: String(explicit).toUpperCase(), assumed: false };
  for (const value of raw) {
    if (value === null || value === undefined) continue;
    const text = String(value);
    for (const [symbol, code] of Object.entries(SYMBOL_CURRENCY)) {
      if (text.includes(symbol)) return { currency: code, assumed: false };
    }
    const match = text.match(CODE_RE);
    if (match) return { currency: match[1].toUpperCase(), assumed: false };
  }
  return { currency: "USD", assumed: true };
}

// Group amounts by currency. items: [{amount, currency, assumed?, label?}].
// Returns {totals: {CUR: n}, assumed: [labels], currencies: [...], mixed}.
export function sumByCurrency(items) {
  const totals = {};
  const assumed = [];
  for (const item of items || []) {
    const amount = Number(item?.amount);
    if (!Number.isFinite(amount) || amount === 0) continue;
    const code = String(item.currency || "USD").toUpperCase();
    totals[code] = Math.round(((totals[code] || 0) + amount) * 100) / 100;
    if (item.assumed && item.label) assumed.push(item.label);
  }
  const currencies = Object.keys(totals).sort();
  return { totals, assumed, currencies, mixed: currencies.length > 1 };
}
